import { Skeleton } from '@zealot/ui/components/skeleton';
import { cn } from '@zealot/ui/lib/utils';
import { cva } from 'class-variance-authority';
import { Pencil } from 'lucide-react';
import * as React from 'react';

const DEFAULT_ERROR_LABEL = 'Save failed — click to retry';

type InlineEditAffordance = 'hover-pencil' | 'double-click';

type InlineEditTriggerPlacement = 'row-end' | 'inline';

type UseInlineEditOptions = {
  isEditing?: boolean;
  defaultEditing?: boolean;
  disabled?: boolean;
  onEditStart?: () => void;
  onCancel?: () => void;
};

type InlineEditTriggerProps = {
  label?: string;
  errorLabel?: string;
  isError?: boolean;
  affordance?: InlineEditAffordance;
  disabled?: boolean;
  className?: string;
  onClick?: () => void;
};

// gloss
type InlineEditProps = UseInlineEditOptions & {
  isSaving?: boolean;
  isError?: boolean;

  restingContent: React.ReactNode;
  editContent: React.ReactNode;
  savingContent?: React.ReactNode;

  affordance?: InlineEditAffordance;
  label?: string;
  errorLabel?: string;
  savingSkeletonClass?: string;

  triggerPlacement?: InlineEditTriggerPlacement;

  className?: string;
  restingClassName?: string;
};

const pencilVariants = cva(
  'shrink-0 rounded p-0.5 transition-opacity text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  {
    variants: {
      affordance: {
        'hover-pencil': 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
        'double-click': 'hidden',
      },
      error: {
        true: 'text-destructive hover:text-destructive opacity-100 group-hover:opacity-100',
        false: 'hover:text-foreground',
      },
    },
    defaultVariants: { affordance: 'hover-pencil', error: false },
  },
);

const restingWrapperVariants = cva('flex items-center gap-1.5 min-w-0', {
  variants: {
    affordance: {
      'hover-pencil': '',
      'double-click':
        'cursor-text rounded px-1 -mx-1 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    },
  },
  defaultVariants: { affordance: 'hover-pencil' },
});

// gloss
const restingValueVariants = cva('min-w-0 truncate font-medium text-foreground', {
  variants: {
    triggerPlacement: {
      'row-end': 'flex-1',
      inline: '',
    },
  },
  defaultVariants: { triggerPlacement: 'row-end' },
});

// gloss
const useInlineEditEscapeGuard = () => {
  const skipRef = React.useRef(false);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      skipRef.current = true;
    }
  };

  const armSkipBlur = () => {
    skipRef.current = true;
  };

  const shouldSkipBlur = () => {
    if (skipRef.current) {
      skipRef.current = false;
      return true;
    }
    return false;
  };

  return { onKeyDown, armSkipBlur, shouldSkipBlur };
};

// gloss
const useInlineEdit = ({
  isEditing: isEditingProp,
  defaultEditing = false,
  disabled = false,
  onEditStart,
  onCancel,
}: UseInlineEditOptions = {}) => {
  const [internalEditing, setInternalEditing] = React.useState(defaultEditing);
  const isControlled = isEditingProp !== undefined;
  const isEditing = isControlled ? isEditingProp : internalEditing;

  const startEditing = () => {
    if (disabled || isEditing) return;

    if (!isControlled) setInternalEditing(true);
    onEditStart?.();
  };

  const cancelEditing = () => {
    if (!isControlled) setInternalEditing(false);
    onCancel?.();
  };

  return {
    isEditing,
    startEditing,
    cancelEditing,
    containerProps: {
      'data-slot': 'inline-edit',
      onKeyDown: (e: React.KeyboardEvent) => {
        if (!isEditing || e.key !== 'Escape') return;

        e.stopPropagation();
        cancelEditing();
      },
    },
    triggerProps: { disabled, onClick: startEditing },
  };
};

const triggerAriaLabel = (label: string | undefined, isError: boolean): string =>
  isError ? `Retry editing${label ? ` ${label}` : ''}` : `Edit${label ? ` ${label}` : ''}`;

// gloss
const InlineEditTrigger = ({
  label,
  errorLabel = DEFAULT_ERROR_LABEL,
  isError = false,
  affordance = 'hover-pencil',
  disabled = false,
  className,
  onClick,
}: InlineEditTriggerProps) => (
  <button
    type="button"
    data-slot="inline-edit-trigger"
    aria-label={triggerAriaLabel(label, isError)}
    title={isError ? errorLabel : undefined}
    onClick={onClick}
    disabled={disabled}
    className={cn(
      pencilVariants({ affordance, error: isError }),
      disabled && 'pointer-events-none opacity-0',
      className,
    )}
  >
    <Pencil className="h-3.5 w-3.5" />
  </button>
);

// gloss
const InlineEdit = ({
  isEditing: isEditingProp,
  defaultEditing = false,
  isSaving = false,
  isError = false,
  disabled = false,
  onEditStart,
  onCancel,
  restingContent,
  editContent,
  savingContent,
  affordance = 'hover-pencil',
  label,
  errorLabel = DEFAULT_ERROR_LABEL,
  savingSkeletonClass = 'h-[1em] w-24 rounded',
  triggerPlacement = 'row-end',
  className,
  restingClassName,
}: InlineEditProps) => {
  const { isEditing, startEditing, containerProps } = useInlineEdit({
    isEditing: isEditingProp,
    defaultEditing,
    disabled,
    onEditStart,
    onCancel,
  });

  const dataAttrs: Record<string, string> = {
    ...(isEditing && { 'data-editing': 'true' }),
    ...(isSaving && { 'data-saving': 'true' }),
    ...(isError && { 'data-error': 'true' }),
    ...(disabled && { 'data-disabled': 'true' }),
  };

  const restingValue = (
    <span className={cn(restingValueVariants({ triggerPlacement }), restingClassName)}>{restingContent}</span>
  );

  if (isSaving) {
    return (
      <div data-slot="inline-edit" aria-busy="true" className={cn('group relative', className)} {...dataAttrs}>
        {savingContent ?? <Skeleton role="status" aria-label="Saving" className={savingSkeletonClass} />}
      </div>
    );
  }

  if (isEditing) {
    return (
      <div className={cn('group relative', className)} {...containerProps} {...dataAttrs}>
        <div data-slot="inline-edit-control">{editContent}</div>
      </div>
    );
  }

  if (affordance === 'double-click') {
    return (
      <div data-slot="inline-edit" className={cn('group relative', className)} {...dataAttrs}>
        <div
          data-slot="inline-edit-resting"
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={triggerAriaLabel(label, isError)}
          aria-disabled={disabled || undefined}
          onDoubleClick={startEditing}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              startEditing();
            }
          }}
          className={cn(restingWrapperVariants({ affordance }), disabled && 'pointer-events-none opacity-50')}
        >
          {restingValue}
        </div>
      </div>
    );
  }

  return (
    <div data-slot="inline-edit" className={cn('group relative', className)} {...dataAttrs}>
      <div data-slot="inline-edit-resting" className={cn(restingWrapperVariants({ affordance }))}>
        {restingValue}
        <InlineEditTrigger
          label={label}
          errorLabel={errorLabel}
          isError={isError}
          affordance={affordance}
          disabled={disabled}
          onClick={startEditing}
        />
      </div>
    </div>
  );
};

// gloss
const inlineEditVariants = {
  pencil: pencilVariants,
  restingWrapper: restingWrapperVariants,
  restingValue: restingValueVariants,
};

export type { InlineEditTriggerPlacement, InlineEditTriggerProps, UseInlineEditOptions };
export {
  InlineEdit,
  InlineEditTrigger,
  inlineEditVariants,
  pencilVariants,
  restingValueVariants,
  restingWrapperVariants,
  useInlineEdit,
  useInlineEditEscapeGuard,
};
